import React, { useState } from 'react';
import { useRecordContext, useGetOne, useNotify, useGetList } from 'react-admin';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Menu as MenuIcon, ChevronRight } from 'lucide-react';
import { Button, ButtonGroup, Menu, MenuItem } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Funeral } from '../types';
import { ClientFilterBar } from './components/ClientFilterBar';

function getStatusBadgeVariant(status?: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' | 'destructive' {
  switch (status) {
    case 'arrangement':
      return 'info';
    case 'transport_dispatched':
    case 'cremation_scheduled':
      return 'warning';
    case 'ready_for_delivery':
    case 'delivery_scheduled':
      return 'secondary';
    case 'delivered':
      return 'success';
    default:
      return 'secondary';
  }
}

function formatStatus(status?: string): string {
  if (!status) return 'Unknown';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface FuneralShowHeaderProps {
  coloredClients?: { id: number; name: string; color: string }[];
  activeClientIds?: Set<number>;
  onToggle?: (clientId: number) => void;
  showSystemEvents?: boolean;
  onToggleSystem?: () => void;
  onMenuClick?: () => void;
  headerActions?: React.ReactNode;
  onStatusClick?: () => void;
}

export function FuneralShowHeader({ coloredClients = [], activeClientIds = new Set(), onToggle, showSystemEvents = true, onToggleSystem, onMenuClick, headerActions, onStatusClick }: FuneralShowHeaderProps) {
  const record = useRecordContext<Funeral>();

  const deceasedClientId = record?.deceased_client_id;

  const { data: deceasedClient } = useGetOne(
    'clients',
    { id: deceasedClientId as number },
    { enabled: !!deceasedClientId },
  );

  const { data: quotes } = useGetList(
    'quotes',
    { filter: { funeral_id: record?.id }, pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } },
    { enabled: !!record?.id },
  );
  const quoteId = quotes?.[0]?.id;

  const notify = useNotify();
  const [copied, setCopied] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const buildClientLink = () => {
    if (!quoteId) return null;
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isDev ? `http://localhost:3005/app/${quoteId}` : `https://cleocremation.com/app/${quoteId}`;
  };
  const handleCopyClientLink = () => {
    const url = buildClientLink();
    if (!url) return;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      notify('Client link copied', { type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    });
  };
  // Open the client link with ?staff=1 so aura-app suppresses the page-view
  // timeline events while staff is previewing.
  const handlePreviewClientLink = () => {
    setMenuAnchor(null);
    const url = buildClientLink();
    if (!url) return;
    window.open(`${url}?staff=1`, '_blank', 'noopener,noreferrer');
  };

  if (!record) return null;

  const deceasedName = deceasedClient?.name || 'Unknown';
  const regionColor = record.region === 'quebec' ? 'bg-blue-100 text-blue-800' : record.region === 'ontario' ? 'bg-red-100 text-red-800' : '';

  return (
    <div className="shrink-0 bg-background">
      <div className="flex items-center justify-between border-b px-3 sm:px-5 py-2 sm:py-3 gap-1.5 sm:gap-2 overflow-hidden">
        {/* Left side */}
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-wrap sm:flex-nowrap">
          <span className="text-foreground whitespace-nowrap" style={{ fontSize: '1.1rem', lineHeight: '1.5rem' }}>
            Cremation for {deceasedName}
          </span>
          <span className="text-xs text-muted-foreground shrink-0">
            #{record.id}
          </span>
          <Badge
            variant={getStatusBadgeVariant(record.cremation_status)}
            className="shrink-0 cursor-pointer hover:opacity-80 inline-flex items-center gap-0.5 text-xs sm:text-sm py-0.5 sm:py-1 px-2 sm:px-2.5"
            onClick={onStatusClick}
          >
            {formatStatus(record.cremation_status)}
            <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Badge>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 shrink-0">
          {record.region && (
            <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded', regionColor)}>
              {record.region === 'quebec' ? 'QC' : 'ON'}
            </span>
          )}
          {quoteId && (
            <>
              <ButtonGroup size="small" variant="outlined" color="inherit">
                <Button
                  onClick={handleCopyClientLink}
                  startIcon={copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <LinkIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontSize: 12, py: 0.25, px: 1 }}
                  title="Copy client dashboard link"
                >
                  {copied ? 'Copied!' : 'Client Link'}
                </Button>
                <Button
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ minWidth: 0, px: 0.5 }}
                  title="More link options"
                >
                  <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                </Button>
              </ButtonGroup>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={handlePreviewClientLink} sx={{ fontSize: 13 }}>Preview Link</MenuItem>
              </Menu>
            </>
          )}
          {headerActions}
          {onMenuClick && (
            <button onClick={onMenuClick} className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <MenuIcon className="h-5 w-5 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
      {onToggle && onToggleSystem && (
        <ClientFilterBar
          clients={coloredClients}
          activeClientIds={activeClientIds}
          onToggle={onToggle}
          showSystemEvents={showSystemEvents}
          onToggleSystem={onToggleSystem}
        />
      )}

    </div>
  );
}
